import { useCallback, useEffect, useState } from 'react'

/**
 * One selection, shared by the home teaser and the full decoder, persisted for the visit.
 * Nothing is lost if she closes the tab and comes back — that accommodation is the point.
 */
const STORAGE_KEY = 'vesta.symptoms'

let selection: string[] = readInitial()
const listeners = new Set<(value: string[]) => void>()

function readInitial(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function persist(value: string[]) {
  selection = value
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    /* Private browsing — the selection simply won't survive a reload. */
  }
  listeners.forEach((listener) => listener(value))
}

export function useSymptomSelection() {
  const [selected, setSelected] = useState<string[]>(selection)

  useEffect(() => {
    const listener = (value: string[]) => setSelected(value)
    listeners.add(listener)
    setSelected(selection)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const toggle = useCallback((id: string) => {
    persist(selection.includes(id) ? selection.filter((item) => item !== id) : [...selection, id])
  }, [])

  const clear = useCallback(() => persist([]), [])

  return { selected, toggle, clear, isSelected: (id: string) => selected.includes(id) }
}
